import { useStripe } from "@stripe/react-stripe-js";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CollapsedBox from "../../../components/CollapsedBox";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../../components/CurrencySelector";
import { useBillingPlans } from "../../../context/billingPlans";
import { useMembers } from "../../../context/members/MembersProvider";
import { useWorkspaces } from "../../../context/workspaces";
import { formatDateToLocale } from "../../../utils/dates";
import { PaymentStep, PlanType } from "../../../utils/types";

const PaymentReview: React.FC = () => {
  const { newPlan, paymentStep, setPaymentStep, createCheckoutSession } =
    useBillingPlans();
  const { currentWorkspace } = useWorkspaces();
  const { isCurrentUserAdmin } = useMembers();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (paymentStep !== PaymentStep.paymentReview) {
      return;
    }

    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [paymentStep]);

  const checkout = useCallback(async () => {
    setLoading(true);

    const response = await createCheckoutSession();

    if (response?.status) {
      await stripe?.redirectToCheckout({ sessionId: response.data.session_id });

      setPaymentStep(PaymentStep.plans);
    }

    setLoading(false);
  }, [createCheckoutSession, setPaymentStep, stripe]);

  return (
    <div ref={containerRef}>
      <CollapsedBox
        className="mt-5 lg:mt-8"
        title="Review & Confirm"
        icon={"3."}
        opened={paymentStep === PaymentStep.paymentReview}
        setOpened={() => setPaymentStep(PaymentStep.paymentReview)}
        disabledSave={
          (newPlan.plan?.type === PlanType.community &&
            currentWorkspace?.active_plan?.type === PlanType.community) ||
          currentWorkspace?.active_plan?.type === newPlan.plan?.type ||
          !isCurrentUserAdmin
        }
        saveButtonName="Complete purchase"
        loadingSave={loading}
        onClickButton={(type) => {
          if (type === "save") {
            checkout();
          }
        }}
      >
        <div className="flex flex-col px-3 sm:px-5 py-5">
          <div className="flex items-center justify-between border-b dark:border-gray-500">
            <span className="py-4 text-sm dark:text-gray-200 ">
              {newPlan.plan?.name} Feature Plan
            </span>
            <span className="font-bold">
              {Number(
                newPlan.plan?.type === currentWorkspace?.active_plan?.type
                  ? "0.00"
                  : `${(newPlan.plan?.price || 0) / 100}`
              ).toLocaleString("en-US", USD_CURRENCY_LOCALE_STRING_CONFIG)}
            </span>
          </div>

          <div className="flex items-center justify-between border-b dark:border-gray-500">
            <span className="py-4 text-sm dark:text-gray-200">
              {newPlan.hotspotAmount.toLocaleString()} hotspots
            </span>
            <span className="font-bold">
              {newPlan.hotspotAmountPrice.toLocaleString(
                "en-US",
                USD_CURRENCY_LOCALE_STRING_CONFIG
              )}
            </span>
          </div>

          <div className="flex items-center justify-between border-b dark:border-gray-500">
            <span className="py-4 text-sm dark:text-gray-200">
              {newPlan.simulationAmount.toLocaleString()} simulations per month
            </span>
            <span className="font-bold">
              {newPlan.simulationAmountPrice.toLocaleString(
                "en-US",
                USD_CURRENCY_LOCALE_STRING_CONFIG
              )}
            </span>
          </div>

          <div className="flex items-center justify-between border-b dark:border-gray-500">
            <span className="py-4 text-sm dark:text-gray-200">
              {newPlan.hotspotAmount * 2 + newPlan.simulationAmount * 2}{" "}
              locations
            </span>
            <span className="font-bold">$0.00</span>
          </div>

          <div className="flex items-center justify-between pt-5">
            <div className="flex flex-col">
              <div className="font-bold sm:text-lg">Total due</div>
              <div className="text-xs">
                for {formatDateToLocale(moment().toString())} to{" "}
                {formatDateToLocale(moment().add(1, "month").toString())}
              </div>
            </div>
            <div className="text-right">
              <div className="font-bold sm:text-lg">
                {Number(newPlan.total).toLocaleString(
                  "en-US",
                  USD_CURRENCY_LOCALE_STRING_CONFIG
                )}
              </div>
              <div className="text-xs">+ VAT if applicable</div>
            </div>
          </div>
        </div>
      </CollapsedBox>
    </div>
  );
};

export default PaymentReview;
